import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { CaretRight, User, BellRinging, BellSlash, Moon } from "@phosphor-icons/react";

export default function CoachClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/coach/clients");
        setClients(data);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="id-label mb-3">— Roster —</div>
      <h1 className="id-h1 mb-10">Clients</h1>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}
      {loading && <div className="id-label id-pulse">Loading...</div>}

      {!loading && clients.length === 0 && (
        <div className="id-card text-center py-16" data-testid="empty-clients">
          <div className="id-label text-zinc-500 mb-2">No clients yet</div>
          <p className="text-zinc-400 font-manrope">Share your signup link with clients to onboard them.</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-px bg-[#262626] border border-[#262626]" data-testid="clients-list">
        {clients.map((c) => (
          <Link
            key={c.id}
            to={`/coach/clients/${c.id}`}
            data-testid={`client-row-${c.id}`}
            className="flex items-center justify-between gap-4 bg-[#0A0A0A] hover:bg-[#121212] px-6 py-5 transition-colors duration-150 group"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center flex-shrink-0">
                <User size={20} weight="bold" className="text-zinc-500" />
              </div>
              <div className="min-w-0">
                <div className="font-oswald text-lg uppercase tracking-wide truncate">{c.name}</div>
                <div className="text-xs text-zinc-500 truncate font-manrope">{c.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Push status pill — tells the coach whether messaging this
                  client will actually buzz their phone or just sit unread. */}
              {c.push_active_subs > 0 ? (
                <span
                  className="hidden sm:inline-flex items-center gap-1 id-label text-[#00CC66] border border-[#00CC66]/40 bg-[#00CC66]/5 px-2 py-1"
                  data-testid={`client-push-on-${c.id}`}
                  title={`${c.push_active_subs} active device${c.push_active_subs === 1 ? "" : "s"}`}
                >
                  <BellRinging size={12} weight="fill" /> On
                </span>
              ) : (
                <span
                  className="hidden sm:inline-flex items-center gap-1 id-label text-zinc-600 border border-[#262626] px-2 py-1"
                  data-testid={`client-push-off-${c.id}`}
                  title="Client hasn't enabled notifications — messaging them won't buzz their phone"
                >
                  <BellSlash size={12} weight="bold" /> Off
                </span>
              )}
              {/* Weekly sleep avg — flags <6h avg red so under-slept clients
                  jump out on the roster. Grey em-dash if no nights logged. */}
              {c.weekly_sleep && c.weekly_sleep.nights_logged > 0 ? (
                <span
                  className={
                    "hidden sm:inline-flex items-center gap-1 id-label border px-2 py-1 " +
                    (c.weekly_sleep.avg_hours < 6
                      ? "text-[#FF3333] border-[#FF3333]/40 bg-[#FF3333]/5"
                      : "text-indigo-300 border-indigo-500/30 bg-indigo-500/5")
                  }
                  data-testid={`client-sleep-${c.id}`}
                  title={`${c.weekly_sleep.total_hours}h total · ${c.weekly_sleep.nights_logged}/7 nights logged this week`}
                >
                  <Moon size={12} weight="fill" /> {c.weekly_sleep.avg_hours}h
                </span>
              ) : (
                <span
                  className="hidden sm:inline-flex items-center gap-1 id-label text-zinc-600 border border-[#262626] px-2 py-1"
                  data-testid={`client-sleep-none-${c.id}`}
                  title="No sleep hours logged this week"
                >
                  <Moon size={12} weight="bold" /> —
                </span>
              )}
              {c.active_program ? (
                <span className="hidden md:inline-block id-label text-[#FF3333] border border-[#FF3333]/40 px-3 py-1">
                  {c.active_program.name}
                </span>
              ) : (
                <span className="hidden md:inline-block id-label text-zinc-600 border border-[#262626] px-3 py-1">
                  Unassigned
                </span>
              )}
              <CaretRight size={18} weight="bold" className="text-zinc-600 group-hover:text-[#FF3333]" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
